import { FC, useState } from "react";

import { AddCourseOptionMenu } from "./AddCourseOptionMenu";
import { showAddCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseGroupingOptionsMenu } from "./CourseGroupingOptionsMenu";
import { CourseListItem } from "./CourseListItem";
import { SectionDetailTermView } from "./SectionDetailTermView";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { EditableField } from "components/hover-widgets/EditableField";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { AddPlusIcon, FolderIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  courseGroupingSelectors,
  courseSelectors,
  selectIsEditingAsCourseAdmin,
  selectIsEditingAsOrgAdmin,
} from "store/selectors";
import { selectCoursesByGroupingId } from "store/selectors/section";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsManager } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";

export const CourseGroupingView: FC = () => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { getQueryParam } = useQueryParamsManager();
  const canEditGrouping = useAppSelector(selectIsEditingAsOrgAdmin);
  const canEditCourse = useAppSelector(selectIsEditingAsCourseAdmin);
  const groupingId = getQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const currentCourseId = getQueryParam(COURSE_ID_QUERY_PARAM);

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  const grouping = useAppSelector((state) => courseGroupingSelectors.selectById(state, groupingId));
  const coursesByGrouping = useListSelector((state) =>
    selectCoursesByGroupingId(state, groupingId),
  );
  const currentCourse = useAppSelector((state) =>
    courseSelectors.selectById(state, currentCourseId),
  );

  // If no grouping_id, show the detail view for the single course
  if (currentCourse && !grouping) {
    return <SectionDetailTermView canEdit={canEditCourse} course={currentCourse} />;
  }

  if (!grouping) {
    return null;
  }

  return (
    <div className="h-full px-7 py-3">
      <h2 className="h2 my-0 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderIcon className="mb-[3px] text-blue" size="l" />

          <EditableField
            isEditing={isEditing}
            label={t("glossary.course_grouping")}
            textSize="h2"
            value={grouping.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) => {
              onTitleChanged("course_groupings", grouping.id, newTitle);
              toastSuccessMessage(t("home.toasts.grouping_renamed"));
            }}
          />
        </div>

        {canEditGrouping && (
          <div className="flex items-center gap-2">
            <CourseGroupingOptionsMenu
              courseGrouping={grouping}
              onRename={() => setIsEditing(true)}
            />

            <AddCourseOptionMenu groupingId={grouping.id} />
          </div>
        )}
      </h2>

      {coursesByGrouping.length === 0 ? (
        <Banner
          buttonChildren={
            canEditGrouping ? (
              <Button
                className="px-2 py-1 text-sm"
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course to Folder`}
                kind="blue"
                onClick={() => showAddCourseModal(grouping.id)}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
              </Button>
            ) : (
              <Button
                className="px-2 py-1 text-sm"
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Enable Edit Mode`}
                kind="blue"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEditGrouping
            ? t("home.courses.empty_grouping_banner_text")
            : t("home.courses.empty_grouping_banner_text_no_edit")}
        </Banner>
      ) : (
        <div role="list">
          {coursesByGrouping.map((course) => (
            <CourseListItem canEdit={canEditCourse} course={course} key={course.id} />
          ))}
        </div>
      )}
    </div>
  );
};
